import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { Dumbbell, Waves, Award, Clock, Zap, Calendar, Info } from "lucide-react";

/**
 * Parent portal — unified "Buy Membership" hub.
 *
 * Three categories in one place:
 *   1. Gym-only monthly / quarterly / annual (fitness-gym subscription)
 *   2. Swim-only monthly / quarterly / annual (pool subscription)
 *   3. Facility-based membership plans (admin-defined: hours / overs / sessions / unlimited)
 *
 * Each type has a distinct EXPIRY model — this page spells it out clearly so
 * parents pick the plan that matches their usage.
 */
export default function ParentMemberships() {
  const [gym, setGym] = useState(null);
  const [swim, setSwim] = useState(null);
  const [flexPlans, setFlexPlans] = useState([]);
  const [myMemberships, setMyMemberships] = useState([]);
  const [loading, setLoading] = useState(true);

  const FIXED_PLANS = [
    { key: "monthly", label: "Monthly", subtitle: "30 days · flexible" },
    { key: "quarterly", label: "Quarterly", subtitle: "90 days · save ~15%" },
    { key: "annual", label: "Annual", subtitle: "365 days · save ~30%" },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const [g, s, plans, mine] = await Promise.all([
        api.get("/gym/my-membership").catch(() => ({ data: null })),
        api.get("/swim/my-membership").catch(() => ({ data: null })),
        api.get("/membership/plans").catch(() => ({ data: [] })),
        api.get("/parent/my-memberships").catch(() => ({ data: [] })),
      ]);
      setGym(g.data);
      setSwim(s.data);
      setFlexPlans(plans.data || []);
      setMyMemberships(mine.data || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const subscribeGym = async (plan) => {
    try {
      await api.post("/gym/subscribe", { plan });
      toast.success("Gym membership activated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const subscribeSwim = async (plan) => {
    try {
      await api.post("/swim/subscribe", { plan });
      toast.success("Swim membership activated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const purchaseFlex = async (planId) => {
    try {
      await api.post("/membership/purchase", { plan_id: planId });
      toast.success("Membership purchased");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="text-white/40 py-16 text-center">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 space-y-14" data-testid="parent-memberships-page">
      <header>
        <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// One place · buy any membership</div>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase leading-none tracking-tighter">
          Memberships<span className="brand-blue">.</span>
        </h1>
        <p className="text-sm text-white/60 mt-4 max-w-2xl">
          Pick from Gym, Swim, or facility-based hourly / over-based plans. Every plan comes with a
          <span className="brand-orange"> unique QR</span> — show it at the gate, the scanner verifies validity and lets you in.
        </p>
      </header>

      {/* GYM */}
      <section data-testid="section-gym">
        <SectionHeader icon={<Dumbbell size={18} />} title="Gym Membership" tag="Fitness · QR entry">
          Unlimited entries during opening hours until the plan ends. QR shown by member is scanned at entry — the scanner only checks
          <span className="brand-orange"> validity + expiry</span>, not workout attendance.
        </SectionHeader>
        {gym ? (
          <ActivePlan title={`${gym.plan.toUpperCase()} · Gym`} member={gym} />
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            {FIXED_PLANS.map((p) => (
              <PriceCard key={p.key} plan={p} price={{ monthly: 1500, quarterly: 3999, annual: 12999 }[p.key]}
                onBuy={() => subscribeGym(p.key)} testid={`buy-gym-${p.key}`} />
            ))}
          </div>
        )}
      </section>

      {/* SWIM */}
      <section data-testid="section-swim">
        <SectionHeader icon={<Waves size={18} />} title="Swim Membership" tag="Pool · QR entry">
          Same QR-at-entry model as gym. Attend as many coached sessions as you like inside the plan window (subject to daily pool cap of 15 swimmers).
        </SectionHeader>
        {swim ? (
          <ActivePlan title={`${swim.plan.toUpperCase()} · Swim`} member={swim} />
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            {FIXED_PLANS.map((p) => (
              <PriceCard key={p.key} plan={p} price={{ monthly: 1800, quarterly: 4800, annual: 15000 }[p.key]}
                onBuy={() => subscribeSwim(p.key)} testid={`buy-swim-${p.key}`} />
            ))}
          </div>
        )}
      </section>

      {/* FACILITY (hours/overs/sessions/unlimited) */}
      <section data-testid="section-flex">
        <SectionHeader icon={<Award size={18} />} title="Facility Memberships" tag="Court · Turf · Ball Machine">
          These plans work on <span className="brand-orange">quotas</span> instead of unlimited entry. When you book a slot or bowling-machine over, the plan burns the equivalent quota.
        </SectionHeader>

        {flexPlans.length === 0 ? (
          <div className="border border-dashed border-white/12 py-10 text-center text-sm text-white/40">
            No facility membership plans available right now — check back later.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {flexPlans.map((p) => (
              <FlexPlanCard key={p.id} plan={p} onBuy={() => purchaseFlex(p.id)} />
            ))}
          </div>
        )}
      </section>

      {/* MY ACTIVE MEMBERSHIPS */}
      {myMemberships.length > 0 && (
        <section data-testid="section-active">
          <SectionHeader icon={<Zap size={18} />} title="Your Active Memberships" tag={`${myMemberships.length} plan(s)`}>
            Live snapshot of what you own and how much quota remains.
          </SectionHeader>
          <div className="space-y-3">
            {myMemberships.map((m) => <ActiveFlexCard key={m.id} m={m} />)}
          </div>
        </section>
      )}

      {/* How the quotas expire */}
      <section className="border border-brand-orange/30 bg-brand-orange/5 p-6">
        <div className="flex items-center gap-2 mb-3 text-brand-orange">
          <Info size={16} />
          <div className="font-mono text-[10px] uppercase tracking-widest">How each plan expires</div>
        </div>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm text-white/70">
          <ExpiryRow icon={<Clock size={14} />} title="Hours plan">
            Ends whichever comes first — <span className="brand-orange">hours quota</span> used up
            <span className="text-white/50"> OR </span>plan <span className="brand-orange">duration_days</span> elapsed. Some plans cap
            <span className="brand-orange"> max_units_per_day</span> (e.g. 2 hours/day) to prevent hoarding.
          </ExpiryRow>
          <ExpiryRow icon={<Zap size={14} />} title="Overs plan">
            Bowling-machine bundles. Each over you play deducts 1 from the balance. Ends when overs quota hits 0 or plan window (usually 30 days) closes.
          </ExpiryRow>
          <ExpiryRow icon={<Calendar size={14} />} title="Sessions plan">
            Buys N coached sessions (e.g. 10 swim sessions). Each check-in burns one session. Ends when the last session is used or the validity window (often 60-90 days) ends.
          </ExpiryRow>
          <ExpiryRow icon={<Award size={14} />} title="Unlimited plan (gym / swim / annual court)">
            No quota — enter freely until the calendar date <span className="brand-orange">end_date</span> is passed (30 / 90 / 365 days from purchase).
          </ExpiryRow>
        </div>
        <div className="mt-4 border-t border-white/10 pt-3 text-[11px] font-mono text-white/40 leading-relaxed">
          <span className="brand-blue">Note on the scanner:</span> The Gym &amp; Swim QR scanner ONLY verifies that a membership exists,
          is active, and has not passed its end_date. It does NOT deduct quota — it only logs an entry timestamp so admins can see visit patterns.
          Quota-based plans (hours / overs / sessions) burn only when you actually <em>book</em> a slot / over / session in the app.
        </div>
      </section>
    </div>
  );
}

/* --- helpers --- */
function SectionHeader({ icon, title, tag, children }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 brand-orange mb-1">
        {icon}
        <div className="font-mono text-[10px] uppercase tracking-[0.3em]">// {tag}</div>
      </div>
      <div className="font-display text-2xl md:text-3xl font-black uppercase">{title}</div>
      <div className="text-sm text-white/60 mt-1 max-w-2xl">{children}</div>
    </div>
  );
}

function PriceCard({ plan, price, onBuy, testid }) {
  return (
    <div className="border border-white/12 bg-[#131A2B] p-5 flex flex-col">
      <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">{plan.subtitle}</div>
      <div className="font-display text-2xl font-black uppercase mt-1">{plan.label}</div>
      <div className="font-display text-3xl font-black brand-blue mt-3">{formatINR(price)}</div>
      <button onClick={onBuy} data-testid={testid}
        className="mt-4 bg-brand-orange text-black py-2.5 font-bold text-xs uppercase tracking-widest hover:bg-white transition">
        Buy Now
      </button>
    </div>
  );
}

function ActivePlan({ title, member }) {
  return (
    <div className="border border-brand-blue/40 bg-[#131A2B] p-6 grid md:grid-cols-2 gap-6">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest brand-blue">Active</div>
        <div className="font-display text-xl font-black uppercase mt-1">{title}</div>
        <div className="text-sm text-white/60 mt-3">Valid until <span className="brand-orange">{member.end_date}</span></div>
        <div className="text-sm text-white/60">Check-ins so far: <span className="brand-blue">{member.check_ins?.length || 0}</span></div>
      </div>
      <div className="flex justify-end">
        <div className="bg-white p-3">
          <QRCodeSVG value={member.qr_token} size={140} bgColor="#ffffff" fgColor="#0B1120" level="M" />
        </div>
      </div>
    </div>
  );
}

function FlexPlanCard({ plan, onBuy }) {
  const isUnlimited = plan.plan_type === "unlimited";
  return (
    <div className="border border-white/12 bg-[#131A2B] p-5" data-testid={`buy-plan-${plan.id}`}>
      <div className="flex justify-between items-start">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">
            {plan.facility_slug} · {plan.plan_type}
          </div>
          <div className="font-display text-lg font-black uppercase mt-1">{plan.name}</div>
        </div>
        <div className="font-display text-2xl font-black brand-blue">{formatINR(plan.price)}</div>
      </div>
      <div className="text-xs text-white/60 mt-3">
        {isUnlimited
          ? <>Unlimited access for <span className="brand-orange">{plan.duration_days} days</span></>
          : <><span className="brand-orange">{plan.quota}</span> {plan.plan_type} valid for <span className="brand-orange">{plan.duration_days} days</span>{plan.max_units_per_day ? <> · max <span className="brand-orange">{plan.max_units_per_day}</span> per day</> : null}</>}
      </div>
      {plan.description && <div className="text-[11px] text-white/40 mt-2">{plan.description}</div>}
      <button onClick={onBuy}
        className="mt-4 w-full bg-brand-orange text-black py-2 font-bold text-xs uppercase tracking-widest hover:bg-white transition">
        Buy for {formatINR(plan.price)}
      </button>
    </div>
  );
}

function ActiveFlexCard({ m }) {
  const total = m.quota_total;
  const remaining = m.quota_remaining ?? total;
  const pct = total ? Math.round(((total - remaining) / total) * 100) : 0;
  return (
    <div className="border border-brand-blue/30 bg-[#131A2B] p-4">
      <div className="flex justify-between flex-wrap items-start gap-3">
        <div>
          <div className="font-display text-sm font-black uppercase">{m.plan_snapshot?.name || m.name}</div>
          <div className="text-[11px] font-mono text-white/50 mt-1">
            Type: <span className="brand-orange">{m.plan_snapshot?.plan_type}</span> · Valid until <span className="brand-orange">{m.valid_until?.slice(0,10)}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">Remaining</div>
          <div className="font-display text-xl brand-blue">
            {total ? `${remaining} / ${total}` : "Unlimited"}
          </div>
        </div>
      </div>
      {total ? (
        <div className="mt-2 w-full bg-white/6 h-1.5">
          <div className="bg-brand-orange h-full transition-all" style={{ width: `${pct}%` }} />
        </div>
      ) : null}
    </div>
  );
}

function ExpiryRow({ icon, title, children }) {
  return (
    <div className="flex gap-2">
      <div className="mt-0.5 text-brand-orange">{icon}</div>
      <div>
        <div className="font-bold text-white/90 text-[13px]">{title}</div>
        <div className="text-xs text-white/60 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}
